import { Bookmark, CirclePlus, Trash2, X } from 'lucide-react';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Template,
  InterviewTemplatePayload,
  Question as BackendQuestion,
  TemplateQuestionInForm,
  InterviewTemplate,
} from '../../types';
import {
  fetchInterviewTemplateById,
  createInterviewTemplate,
  updateInterviewTemplate,
} from '../../services/interviewTemplateService';

import { Button } from '../../components/Common/button';
import { v4 as uuidv4 } from 'uuid';

const TemplateForm: React.FC = () => {
  const { t } = useTranslation();
  const { id: templateIdFromParams } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = Boolean(templateIdFromParams);

  const initialTemplateState: Partial<Template> & { questions: TemplateQuestionInForm[] } = {
    name: '',
    description: '',
    questions: [],
  };

  const [template, setTemplate] = useState<Partial<Template> & { questions: TemplateQuestionInForm[] }>(initialTemplateState);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isEditing && templateIdFromParams) {
      setIsLoading(true);
      setError(null);
      fetchInterviewTemplateById(templateIdFromParams)
        .then((backendData: InterviewTemplate) => {
          setTemplate({
            id: backendData.id,
            name: backendData.name,
            description: backendData.description || '',
            questions: backendData.questions.map((q: BackendQuestion): TemplateQuestionInForm => ({
              id: uuidv4(),
              text: q.text,
              order: q.order,
            })),
            createdAt: (backendData as any).createdAt,
            updatedAt: (backendData as any).updatedAt,
          });
          setIsLoading(false);
        })
        .catch(err => {
          console.error("Erreur lors du chargement du template:", err);
          setError(t('templates.loadError'));
          setIsLoading(false);
          // Optionnel: rediriger si le template n'est pas trouvé (ex: err.response.status === 404)
          // navigate('/templates');
        });
    } else {
      if (template.questions.length === 0) {
        setTemplate(prev => ({ ...prev, questions: [{ id: uuidv4(), text: '', order: 1 }] }));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateIdFromParams, isEditing, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setTemplate(prev => ({ ...prev, [name]: value }));
  };

  const handleQuestionChange = (questionId: string, field: 'text' | 'order', value: string | number) => {
    setTemplate(prev => ({
      ...prev,
      questions: prev.questions.map(q =>
        q.id === questionId
          ? { ...q, [field]: field === 'order' ? Number(value) || 1 : value } // Assurer une valeur numérique pour order, défaut à 1
          : q
      ),
    }));
  };

  const handleAddQuestion = () => {
    setTemplate(prev => {
      // Assurer que prev.questions existe et est un tableau avant d'utiliser map
      const currentQuestions = prev.questions || [];
      const nextOrder = currentQuestions.length > 0 ? Math.max(...currentQuestions.map(q => q.order)) + 1 : 1;
      const newQuestion: TemplateQuestionInForm = { id: uuidv4(), text: '', order: nextOrder };
      return {
        ...prev,
        questions: [...currentQuestions, newQuestion],
      };
    });
  };

  const handleRemoveQuestion = (questionId: string) => {
    setTemplate(prev => ({
      ...prev,
      // Assurer que prev.questions existe avant de filtrer
      questions: (prev.questions || []).filter(q => q.id !== questionId),
    }));
  };

  const isFormInvalid = useCallback(() => {
    return !template.name?.trim() ||
           !template.questions ||
           template.questions.length === 0 ||
           template.questions.some(q => !q.text.trim() || typeof q.order !== 'number' || q.order <= 0);
  }, [template.name, template.questions]);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (isFormInvalid()) {
      setError(t('templates.nameRequired'));
      return;
    }

    setIsLoading(true);

    // Mapper vers le type attendu par le backend (sans l'ID client)
    const payloadQuestions: { text: string; order: number }[] = template.questions.map(q => ({
      text: q.text,
      order: q.order, // order est maintenant requis et un nombre
    }));

    const payload: InterviewTemplatePayload = {
      name: template.name!,
      description: template.description || null,
      template_type: 'cadrage',
      questions: payloadQuestions,
    };
    try {
      if (isEditing && templateIdFromParams) {
        await updateInterviewTemplate(templateIdFromParams, payload);
      } else {
        await createInterviewTemplate(payload);
      }
      navigate('/templates');
    } catch (err: any) {
      console.error("Erreur lors de la sauvegarde du template:", err);
      setError(err.response?.data?.detail || err.message || t('templates.updateError'));
    } finally {
      setIsLoading(false);
    }
  };

  // Affichage du chargement uniquement pendant la récupération initiale en mode édition
  if (isLoading && isEditing && !template.name) {
    return <div className="p-6 text-center">{t('templates.loading')}</div>;
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <h1 className="text-2xl font-bold text-foreground">
        {isEditing ? t('templates.editTemplate') : t('templates.newTemplate')}
      </h1>

      {error && (
        <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">{t('templates.errorTitle')}</strong>
          <span className="block sm:inline"> {error}</span>
          <Button onClick={() => setError(null)} variant="ghost" size="icon" className="absolute top-0 bottom-0 right-0">
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6 bg-card p-6 rounded-lg shadow-sm border border-border">
        {/* Nom */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-foreground mb-1">
            {t('templates.nameLabel')}
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={template.name || ''}
            onChange={handleChange}
            required
            className="block w-full px-3 py-2 border border-input bg-background text-foreground rounded-md shadow-sm focus:outline-none focus:ring-ring focus:border-ring sm:text-sm"
          />
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-foreground mb-1">
            {t('templates.descriptionOptional')}
          </label>
          <textarea
            id="description"
            name="description"
            rows={3}
            value={template.description || ''}
            onChange={handleChange}
            className="block w-full px-3 py-2 border border-input bg-background text-foreground rounded-md shadow-sm focus:outline-none focus:ring-ring focus:border-ring sm:text-sm"
          />
        </div>

        {/* Questions guidées */}
        <div className="space-y-4 border-t border-border pt-4 mt-4">
          <h3 className="text-md font-medium text-foreground">{t('templates.guidedQuestions')}</h3>
          {template.questions?.map((questionItem) => (
              <div key={questionItem.id} className="p-3 border border-border rounded-md bg-muted space-y-2">
                <div className="flex items-end space-x-2">
                  <div className="flex-grow">
                    <label htmlFor={`qtext-${questionItem.id}`} className="block text-xs font-medium text-muted-foreground">
                      {t('templates.questionText')}
                    </label>
                    <textarea
                      id={`qtext-${questionItem.id}`}
                      rows={2}
                      value={questionItem.text}
                      onChange={(e) => handleQuestionChange(questionItem.id, 'text', e.target.value)}
                      required
                      className="block w-full mt-1 px-2 py-1.5 border border-input bg-background text-foreground rounded-md shadow-sm focus:outline-none focus:ring-ring focus:border-ring sm:text-sm"
                    />
                  </div>
                  <div className="w-24">
                    <label htmlFor={`qorder-${questionItem.id}`} className="block text-xs font-medium text-muted-foreground">
                      {t('templates.order')}
                    </label>
                    <input
                      id={`qorder-${questionItem.id}`}
                      type="number"
                      min="1"
                      value={questionItem.order} // order est maintenant un nombre
                      onChange={(e) => handleQuestionChange(questionItem.id, 'order', parseInt(e.target.value, 10))}
                      required
                      className="block w-full mt-1 px-2 py-1.5 border border-input bg-background text-foreground rounded-md shadow-sm focus:outline-none focus:ring-ring focus:border-ring sm:text-sm"
                    />
                  </div>
                  <Button
                    type="button"
                    onClick={() => handleRemoveQuestion(questionItem.id)}
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive"
                    aria-label={t('templates.removeQuestionAria')}
                    disabled={!template.questions || template.questions.length <= 1}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
          ))}
          <Button
            type="button"
            onClick={handleAddQuestion}
            variant="outline"
            className="inline-flex items-center border-dashed"
          >
            <CirclePlus className="h-4 w-4 mr-2" />
            {t('templates.addQuestion')}
          </Button>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 border-t border-border pt-6 mt-6">
          <Button
            type="button"
            onClick={() => navigate('/templates')}
            variant="outline"
            disabled={isLoading}
          >
            {t('templates.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={isLoading || isFormInvalid()}
          >
            <Bookmark className="h-4 w-4 mr-2" />
            {isLoading ? t('templates.saveInProgress') : (isEditing ? t('templates.update') : t('templates.create'))}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default TemplateForm;
